import {guard, sample} from 'effector';

import {doParseXml} from '../../components/helpers/buildXml';
import {RestError, showRestError} from '../../core/errors';
import {InvocieLineHolder, InvoiceLine} from '../../types/invoices';

import {
    $fetchDataFxFailed,
    $sellerTaxNumber,
    setInvoiceState,
    setInvoiceTypeCodeStatusOnInitialFetch,
} from '../common';
import {
    addInvoiceLineValues,
    arrStores,
    setInvoiceLinesHolder,
    setOriginalInvoiceSnapshot,
    setTaxesAndTotalFromXml,
    updateInfoValues,
    updatePageValues,
} from '../create';

import {calculateCopyActions} from '../create/init';

import {
    fetchDataFx,
    commonData$,
    $inlineInvoiceChargeModal,
    setInlineInvoiceChargeModal,
    $modalDuplicateLinesModal,
    setModalDuplicateLinesModal,
    $inlineModalFormReadonly,
    setInlineModalFormReadonly,
    $invoiceCreatingPending,
    setInvoiceCreatingPending,
} from './index';

$inlineInvoiceChargeModal.on(setInlineInvoiceChargeModal, (_, data) => data);
$inlineModalFormReadonly.on(setInlineModalFormReadonly, (_, data) => data);

fetchDataFx.fail.watch(({error}) => showRestError(error as RestError));
$fetchDataFxFailed.on(fetchDataFx.fail, () => true);

$modalDuplicateLinesModal.on(setModalDuplicateLinesModal, (_, data) => data);

sample({
    clock: setInvoiceState,
    filter: (i: string | null): i is string => !!i && i !== 'new',
    fn: (id) => ({
        id,
        tin: $sellerTaxNumber.getState() ?? undefined,
    }),
    target: fetchDataFx,
});

commonData$.on(fetchDataFx.doneData, (_, data) => data.body);

fetchDataFx.done.watch((val) => {
    setInvoiceTypeCodeStatusOnInitialFetch(val.result.docType);
});

fetchDataFx.doneData.watch(({body: p}) => {
    const parsedXml = doParseXml(p);
    const {generalInformation, relatedDocuments, invoiceLines, taxes} = parsedXml['invoice'];

    updateInfoValues(generalInformation);
    updatePageValues(relatedDocuments);
    // setInvoiceLineChargeIndicator(invoiceLines.itemChargeIndicator);

    const inoviceLinesValues = Object.values(invoiceLines) as InvoiceLine[];
    const arrayReducedWithCombinedCodeId = inoviceLinesValues.reduce(
        (acc, i) => {
            const {combinedCodeId, ...rest} = i as InvocieLineHolder;
            if (combinedCodeId === undefined) {
                acc.array.push(rest);
                return acc;
            }

            const codeId = acc.combinedCodeIndexes[combinedCodeId];
            if (codeId !== undefined) {
                const {charges, vat, qty, price} = acc.array[codeId];
                const calcQty = Number(qty) + 1;
                const calcPrice = Number(price);
                const chargeAmount = charges
                    ? charges.amountXaf * (charges.isCharge === 'true' ? 1 : -1)
                    : 0;

                const lines = calcQty * calcPrice + chargeAmount;
                const lineAmountWVat = (Number(vat) / 100) * lines + lines;

                acc.array[codeId] = {
                    ...acc.array[codeId],
                    qty: calcQty.toString(),
                    lines: lines.toFixed(2),
                    lineAmountWVat: lineAmountWVat.toFixed(2),
                    combinedCodes: [...(acc.array[codeId]?.combinedCodes ?? []), rest.code ?? ''],
                    combinedCodeId,
                };
            } else {
                acc.combinedCodeIndexes[combinedCodeId] = acc.array.length;
                const valueToArray = {
                    ...rest,
                    ...(rest.code && {combinedCodes: [rest.code]}),
                };
                acc.array.push(valueToArray);
            }

            return acc;
        },
        {
            array: [] as InvocieLineHolder[],
            combinedCodeIndexes: {} as Record<string, number>,
        },
    );

    const flatArray = arrayReducedWithCombinedCodeId.array;
    const byCode = new Map<string, InvocieLineHolder>();
    const roots: InvocieLineHolder[] = [];

    for (const item of flatArray) {
        const node: InvocieLineHolder = {...item, subRows: item.subRows ?? []};
        const {parent: parentCode, level} = item;

        if (level === 0 || level === 1 || parentCode == null || parentCode === '') {
            const copyActions = calculateCopyActions({
                ...node,
                copyActions: {
                    allowToDelete: true,
                    allowToCopy: false,
                    allowToDuplicateDown: false,
                    allowToPaste: false,
                },
            });
            roots.push({
                ...node,
                copyActions,
            });
        } else {
            const parent = byCode.get(parentCode);
            if (parent) {
                (parent.subRows = parent.subRows ?? []).push({
                    ...node,
                    copyActions: calculateCopyActions(node),
                });
            } else {
                roots.push({
                    ...node,
                    copyActions: calculateCopyActions(node),
                });
            }
        }
        if (item.code) byCode.set(item.code, node);
    }

    setTaxesAndTotalFromXml(taxes);

    addInvoiceLineValues(inoviceLinesValues);
    setInvoiceLinesHolder(roots);
    setOriginalInvoiceSnapshot(structuredClone(arrStores.getState()));
});

$invoiceCreatingPending.on(setInvoiceCreatingPending, (_, data) => data);
